/**
 * /view/estimate/:token (tracker item 39): the estimate behind an emailed
 * link, outside the app's sign-in and nav. The customer can approve it
 * (typing their name, like a signature) or decline it while it waits for an
 * answer. Every unusable link shows the same "not available" page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createAPIClient, IEstimatesApi } from '../../api';
import * as types from '../../api/types';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { invoiceDate } from '../invoiceUi';
import { CUSTOMER_STATUS_LABELS, estimateErrorText, estimateStatusClass } from '../estimates/estimateUi';
import { CustomerEstimateBody } from '../portal/PortalEstimatePages';
import { PublicLayout, Unavailable } from './PublicDocumentPages';

export function PublicEstimatePage() {
  const { token = '' } = useParams<{ token: string }>();
  const api = useMemo<IEstimatesApi>(() => createAPIClient(), []);
  const [state, setState] = useState<{ data: types.PublicEstimateView | null; loading: boolean }>({ data: null, loading: true });
  const [mode, setMode] = useState<null | 'approve' | 'decline'>(null);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api.getPublicEstimate(token)
      .then((data) => { if (active) setState({ data, loading: false }); })
      .catch(() => { if (active) setState({ data: null, loading: false }); });
    return () => { active = false; };
  }, [api, token]);

  const estimate = state.data;
  if (!estimate) return <Unavailable loading={state.loading} />;

  async function answer(decision: types.EstimateDecision) {
    if (decision === 'APPROVED' && !name.trim()) { setError('Type your name to approve the estimate.'); return; }
    setBusy(true);
    setError(null);
    try {
      const saved = await api.respondPublicEstimate(token, decision, { name: name.trim() || null, comment: comment.trim() || null });
      setState({ data: saved, loading: false });
      setMode(null);
      setDone(decision === 'APPROVED' ? 'Thank you: the estimate is approved. We will contact you to schedule the work.' : 'The estimate was declined. Thank you for letting us know.');
    } catch (cause) {
      setError(estimateErrorText(cause, 'We could not record your answer. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <PublicLayout title={`Estimate ${estimate.estimateNumber} - ${estimate.company.companyName}`} company={estimate.company}>
      <section className="print-keep mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 break-words">
          <h1 className="text-2xl font-bold tracking-wide">ESTIMATE</h1>
          <p className="mt-1 text-base font-semibold">#{estimate.estimateNumber}</p>
          {estimate.title && <p className="mt-1">{estimate.title}</p>}
          <p className="mt-3"><span className="text-gray-600">For </span><span className="font-semibold">{estimate.customerName}</span></p>
          <p className="text-gray-700">{estimate.propertyAddress}</p>
        </div>
        <div className="space-y-2">
          <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${estimateStatusClass(estimate.status)}`} data-testid="public-estimate-status">
            {CUSTOMER_STATUS_LABELS[estimate.status]}
          </span>
          <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
            {estimate.issueDate && <><dt className="text-gray-600">Date</dt><dd>{invoiceDate(estimate.issueDate)}</dd></>}
            <dt className="text-gray-600">Valid until</dt><dd>{invoiceDate(estimate.expiryDate)}</dd>
          </dl>
        </div>
      </section>

      <CustomerEstimateBody estimate={estimate} />

      <div className="print:hidden mt-8 space-y-3">
        {done && <p role="status" className="rounded bg-green-50 p-3 text-sm text-green-800">{done}</p>}
        {error && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        {estimate.canRespond && mode === null && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" className={primaryButtonClass} onClick={() => setMode('approve')}>Approve estimate</button>
            <button type="button" className={secondaryButtonClass} onClick={() => setMode('decline')}>Decline</button>
          </div>
        )}
        {estimate.canRespond && mode !== null && (
          <div className="rounded border border-gray-200 p-4">
            <label htmlFor="public-estimate-name" className={labelClass}>{mode === 'approve' ? 'Your name (required)' : 'Your name (optional)'}</label>
            <input id="public-estimate-name" className={inputClass} maxLength={200} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            <label htmlFor="public-estimate-comment" className={`${labelClass} mt-3`}>{mode === 'approve' ? 'Anything we should know? (optional)' : 'Why are you declining? (optional)'}</label>
            <textarea id="public-estimate-comment" className={inputClass} rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
            {mode === 'approve' && <p className="mt-2 text-xs text-gray-600">By approving you accept this estimate for the work and prices above.</p>}
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void answer(mode === 'approve' ? 'APPROVED' : 'DECLINED')}>
                {busy ? 'Sending…' : mode === 'approve' ? 'Confirm approval' : 'Confirm decline'}
              </button>
              <button type="button" className={secondaryButtonClass} onClick={() => setMode(null)}>Back</button>
            </div>
          </div>
        )}
        {!estimate.canRespond && estimate.status === 'EXPIRED' && (
          <p className="text-sm text-gray-700">This estimate has expired. Please contact us for an updated one.</p>
        )}
      </div>
    </PublicLayout>
  );
}
